/* eslint-disable jsx-a11y/label-has-for */
/* <label> requires input present. The input is inside DatePicker, unreadable by eslint */
import React, { isValidElement, useMemo, useState } from 'react';
import { bool, func, instanceOf, oneOf, shape, string } from 'prop-types';
import { cx } from '@emotion/css';
import DatePicker, { registerLocale } from 'react-datepicker';
import localeId from 'date-fns/locale/id';
import 'react-datepicker/dist/react-datepicker.css';

import IconCalendar from '@/assets/Icon/svg/Calendar';
import { colors } from '@/colors';
import Typography from '@/components/Typography';

import { styDateWrapper, styTextInput, styTextAppend } from './style';

registerLocale('id', localeId);

const InputDate = (props) => {
  const {
    id,
    name,
    block,
    bold,
    className,
    dateFormat,
    defaultValue,
    disabled,
    error,
    minDate,
    maxDate,
    onBlur,
    onChange,
    onEnter,
    onKeyDown,
    placeholder,
    showMonthDropdown,
    showYearDropdown,
    size,
    style,
    textHint,
    value: propsValue,
    borderColor,
    ...inputProps
  } = props;

  // Store ref to element using state to trigger re-render on mount
  const [appendEl, setAppendEl] = useState(null);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== undefined, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = (date) => {
    const event = { ...new Event('change') };
    event.target = { value: date };
    onChange(event);

    if (!isControlled) setStateValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onEnter();
    }
    onKeyDown(event);
  };

  const datepickerProps = {
    locale: 'id',
    dateFormat,
    disabled,
    minDate,
    maxDate,
    placeholder,
    showMonthDropdown,
    showYearDropdown,
    useShortMonthInDropdown: true,
    autoComplete: 'off',
  };

  const styProps = {
    size,
    block,
    disabled,
    bold,
    error,
    append: true,
    appendPadding: appendEl?.offsetWidth || 36,
    borderColor,
  };

  const renderAppend = (elem, spanProps) =>
    elem && (
      <span {...spanProps}>
        {isValidElement(elem) ? (
          elem
        ) : (
          <Typography type="body" bold={bold} style={{ margin: 0 }}>
            {elem}
          </Typography>
        )}
      </span>
    );

  const renderMessage = () => {
    switch (true) {
      case !!error:
        // If error exists, show error as highest priority
        return (
          <div>
            <Typography type="alert" alertType="error" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          </div>
        );
      case !!textHint:
        // If no higher priorities present, display textHint if available
        return (
          <div>
            <Typography type="label" style={{ marginTop: 4 }}>
              {textHint}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <label htmlFor={id} className={styDateWrapper(styProps)}>
      <DatePicker
        id={id}
        data-testid={id}
        name={name}
        style={style}
        className={cx(styTextInput(styProps), className)}
        // Component is always controlled
        // If value is provided, use the provided value. Otherwise, use value stored on state
        selected={isControlled ? propsValue : stateValue}
        // Do not pass defaultValue
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        // react-datepicker cannot pass data-* props directly, so use customInput as a workaround
        // See more at https://github.com/Hacker0x01/react-datepicker/issues/1757
        customInput={<input data-testid={id} {...inputProps} />}
        {...datepickerProps}
      />
      {renderAppend(<IconCalendar color={colors.NEUTRAL_10} />, {
        className: styTextAppend(styProps),
        ref: (v) => setAppendEl(v),
      })}
      {renderMessage()}
    </label>
  );
};

InputDate.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  block: bool,
  bold: bool,
  className: string,
  dateFormat: string,
  defaultValue: instanceOf(Date),
  disabled: bool,
  error: string,
  minDate: instanceOf(Date),
  maxDate: instanceOf(Date),
  onBlur: func,
  onChange: func,
  onEnter: func,
  onKeyDown: func,
  placeholder: string,
  showMonthDropdown: bool,
  showYearDropdown: bool,
  size: oneOf(['sm', 'md']),
  style: shape({}),
  textHint: string,
  value: instanceOf(Date),
  borderColor: string,
};

InputDate.defaultProps = {
  block: false,
  bold: false,
  className: '',
  dateFormat: 'dd MMM yyyy',
  defaultValue: null,
  disabled: false,
  error: '',
  minDate: null,
  maxDate: null,
  onBlur: () => {},
  onChange: () => {},
  onEnter: () => {},
  onKeyDown: () => {},
  placeholder: '',
  showMonthDropdown: true,
  showYearDropdown: true,
  size: 'md',
  style: {},
  textHint: '',
  value: undefined,
  borderColor: '',
};

export default InputDate;
